import { getTriggerEventLabels } from '@/lib/api/sales/automations-api'
import { getSalesFlows, type SalesFlow, type SalesFlowTrigger } from '@/lib/api/sales/flows-api'
import type { PipelineStage, SalesPipeline } from '@/lib/api/sales/pipelines-api'
import { useServiceLabel } from '@/lib/hooks/use-service-label'
import { FLOW_TRIGGER_LABELS, formatMinutes } from '@/pages/admin/sales/flows/flow-meta'
import { useEffect, useMemo, useState } from 'react'
import { MdClose, MdOpenInNew } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'

type FlowHit = { flow: SalesFlow; triggers: SalesFlowTrigger[] }

/**
 * Visão por funil (docs/sales-flows.md, seção 7): quais fluxos disparam em cada etapa.
 * Só leitura; editar é no editor do fluxo. Considera os gatilhos publicados.
 */
export default function PipelineFlowsModal({ pipeline, stages, onClose }: { pipeline: SalesPipeline; stages: PipelineStage[]; onClose: () => void }) {
    const navigate = useNavigate()
    const eventLabels = getTriggerEventLabels(useServiceLabel()) as Record<string, string>
    const [flows, setFlows] = useState<SalesFlow[] | null>(null)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        getSalesFlows().then(res => {
            if (res.success) setFlows(res.result)
            else setError(res.error || 'Não foi possível carregar os fluxos')
        })
    }, [])

    const { byStage, wholePipeline, allPipelines } = useMemo(() => {
        const stageIds = new Set(stages.map(s => s.id))
        const byStage = new Map<number, FlowHit[]>()
        const wholePipeline: FlowHit[] = []
        let allPipelines = 0
        const add = (list: FlowHit[], flow: SalesFlow, trigger: SalesFlowTrigger) => {
            const hit = list.find(h => h.flow.id === flow.id)
            if (hit) hit.triggers.push(trigger)
            else list.push({ flow, triggers: [trigger] })
        }
        for (const flow of flows ?? []) {
            let global = false
            for (const trigger of flow.triggers) {
                if (trigger.stageId && stageIds.has(trigger.stageId)) {
                    const list = byStage.get(trigger.stageId) ?? []
                    add(list, flow, trigger)
                    byStage.set(trigger.stageId, list)
                } else if (!trigger.stageId && trigger.pipelineId === pipeline.id) {
                    add(wholePipeline, flow, trigger)
                } else if (!trigger.stageId && !trigger.pipelineId && trigger.type !== 'MANUAL') {
                    global = true
                }
            }
            if (global) allPipelines++
        }
        return { byStage, wholePipeline, allPipelines }
    }, [flows, stages, pipeline.id])

    const triggerLabel = (t: SalesFlowTrigger) => {
        const label = t.type === 'EVENT' && t.event ? (eventLabels[t.event] ?? t.event) : FLOW_TRIGGER_LABELS[t.type]
        return t.delayMinutes ? `${label} · ${formatMinutes(t.delayMinutes)}` : label
    }

    const flowCard = ({ flow, triggers }: FlowHit) => (
        <button
            key={flow.id}
            type="button"
            className="w-full rounded-lg border border-base-300 bg-base-100 p-3 text-left transition hover:border-primary hover:shadow-sm"
            onClick={() => navigate(`/sales/automations/flows/${flow.id}`)}
        >
            <span className="flex items-start justify-between gap-2">
                <span className="text-sm font-medium leading-snug">{flow.name}</span>
                <MdOpenInNew className="mt-0.5 shrink-0 text-base-content/40" />
            </span>
            <span className="mt-2 flex flex-wrap gap-1">
                {triggers.map((t, i) => (
                    <span key={i} className="badge badge-outline badge-sm h-auto py-0.5 text-left">
                        {triggerLabel(t)}
                    </span>
                ))}
            </span>
            <span className={`mt-2 block text-xs ${flow.active ? 'text-success' : 'text-base-content/50'}`}>{flow.active ? 'Ativo' : 'Desativado'}</span>
        </button>
    )

    const sortedStages = [...stages].sort((a, b) => a.sortOrder - b.sortOrder)

    return (
        <dialog className="modal modal-open z-50">
            <div className="modal-box flex max-h-[90vh] w-11/12 max-w-6xl flex-col">
                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        <h3 className="text-lg font-bold">Fluxos do funil {pipeline.name}</h3>
                        <p className="text-sm text-base-content/60">Quais fluxos disparam em cada etapa. Clique em um fluxo para abrir o editor.</p>
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm btn-square" onClick={onClose} aria-label="Fechar">
                        <MdClose className="text-lg" />
                    </button>
                </div>

                {error ? (
                    <p className="py-8 text-center text-sm text-error">{error}</p>
                ) : !flows ? (
                    <div className="flex justify-center py-12">
                        <span className="loading loading-spinner" />
                    </div>
                ) : (
                    <div className="min-h-0 flex-1 space-y-4 overflow-y-auto">
                        {wholePipeline.length > 0 && (
                            <section className="rounded-xl border border-base-300 bg-base-200/40 p-4">
                                <h4 className="mb-3 text-sm font-semibold">Em qualquer etapa deste funil</h4>
                                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">{wholePipeline.map(flowCard)}</div>
                            </section>
                        )}

                        <div className="overflow-x-auto pb-2">
                            <div className="flex gap-3">
                                {sortedStages.map(stage => {
                                    const hits = byStage.get(stage.id) ?? []
                                    return (
                                        <section key={stage.id} className="flex w-64 shrink-0 flex-col rounded-xl border border-base-300 bg-base-200/40">
                                            <header className="border-b border-base-300 px-3 py-2" style={stage.color ? { borderTop: `3px solid ${stage.color}`, borderTopLeftRadius: 12, borderTopRightRadius: 12 } : undefined}>
                                                <span className="block truncate text-sm font-semibold">{stage.name}</span>
                                                <span className="text-xs text-base-content/50">
                                                    {hits.length ? `${hits.length} ${hits.length === 1 ? 'fluxo' : 'fluxos'}` : 'Nenhum fluxo'}
                                                </span>
                                            </header>
                                            <div className="flex flex-col gap-2 p-2">{hits.map(flowCard)}</div>
                                        </section>
                                    )
                                })}
                            </div>
                        </div>

                        {allPipelines > 0 && (
                            <p className="text-xs text-base-content/60">
                                {allPipelines === 1 ? '1 fluxo dispara' : `${allPipelines} fluxos disparam`} por evento sem funil definido e vale{allPipelines === 1 ? '' : 'm'} também para este funil.
                                Veja em Automações e fluxos.
                            </p>
                        )}
                    </div>
                )}
            </div>
            <div className="modal-backdrop" onClick={onClose} />
        </dialog>
    )
}
